/// <amd-module name="DistributedTaskUI/Logs/LogsViewUtility" />

import {
    ILiveLogsData,
    ILogLine,
    Issue,
    IssueType,
    IStatus,
    ITaskLog,
    JobStates,
    LogType,
    TaskState,
} from "DistributedTaskUI/Logs/Logs.Types";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { localeFormat, format } from "VSS/Core/Util/String";
import { Uri } from "VSS/Platform/Util/Url";

import { IStatusProps, Statuses } from "VSSUI/Status";

const _logFileLineNumber: string = "logFileLineNumber";

const toStatus = (name: string, icon: string, state: string, statusProps: IStatusProps): IStatus => {
    return {
        status: name,
        iconName: icon,
        className: "state-" + state,
        statusProps: statusProps
    };
};

export class LogsViewUtility {

    public static getTaskStatus(state: TaskState, errorCount: number, percentComplete?: number): IStatus {
        const errorInfo = (defaultText: string): string => {
            return errorCount > 0 ? localeFormat(this._getErrorString(errorCount), errorCount) : defaultText;
        };

        switch (state) {
            case TaskState.Succeeded:
                return toStatus(Resources.Succeeded, "Accept", "success", Statuses.Success);
            case TaskState.Failed:
                return toStatus(errorInfo(Resources.JobFailedStatus), "Cancel", "failed", Statuses.Failed);
            case TaskState.InProgress:
                let status = "";
                if (percentComplete) {
                    status = format(Resources.PassPercentage, percentComplete);
                }
                return toStatus(status, "Play", "inProgress", Statuses.Running);
            case TaskState.Unknown:
            case TaskState.Pending:
                return toStatus(Resources.Pending, "Clock", "pending", Statuses.Waiting);
            case TaskState.Skipped:
                return toStatus(Resources.Skipped, "Blocked", "skipped", Statuses.Skipped);
            case TaskState.PartiallySucceeded:
                return toStatus(errorInfo(Resources.JobPartiallySucceededStatus), "Warning", "partially-success", Statuses.Warning);
            case TaskState.Cancelled:
                return toStatus(Resources.Cancelled, "CircleStopSolid", "cancelled", Statuses.Canceled);
            default:
                return {
                    iconName: "",
                    className: "",
                    status: "",
                    statusProps: Statuses.Queued
                };
        }
    }

    public static getWarningStatus(warningCount: number): IStatus | null {
        if (warningCount > 0) {
            return {
                iconName: "Warning",
                className: "state-partially-success",
                status: localeFormat(this._getWarningString(warningCount), warningCount),
                statusProps: Statuses.Warning
            };
        }

        return null;
    }

    public static getPhaseStatusFromJobStates(jobState: JobStates): IStatus | null {

        switch (jobState) {
            case JobStates.Pending:
                return toStatus(Resources.PhaseNotStartedText, "Clock", "pending", Statuses.Waiting);
            case JobStates.Cancelled:
                return toStatus(Resources.PhaseCancelledText, "CircleStopSolid", "cancelled", Statuses.Canceled);
            case JobStates.Cancelling:
                return toStatus(Resources.PhaseCancellingText, "CircleStopSolid", "cancelled", Statuses.Canceled);
            case JobStates.Failed:
                return toStatus(Resources.PhaseFailedText, "Cancel", "failed", Statuses.Failed);
            case JobStates.Skipped:
                return toStatus(Resources.PhaseSkippedText, "Blocked", "skipped", Statuses.Skipped);
            default:
                return null;
        }
    }

    public static getLogSectionIcon(logType?: LogType): string {
        // Showing Icon for Warning and Error only. We can expand this list on need basis
        switch (logType) {
            case LogType.Error:
                return "bowtie-status-failure";
            case LogType.Warning:
                return "bowtie-status-warning";
            default:
                return "";
        }
    }

    public static getQueueUrl(queueId: number | undefined, pageContext: IVssPageContext | undefined): string {
        return this._getUrlForRoute(pageContext, "ms.vss-build-web.agent-queues-route", { "_a": "agents", "queueId": queueId });
    }

    public static getConcurrentJobsLimitUrl(pageContext: IVssPageContext | undefined): string {
        return this._getUrlForRoute(pageContext, "ms.vss-build-web.build-release-collection-admin-route", { "_a": "concurrentJobs" });
    }

    public static showExpandViewEnabled(task: ITaskLog): boolean {
        let isExpandLogsViewEnabled: boolean = false;
        if (task) {
            if (this.isLiveComponentVisible(task)) {
                // If task is in progress, the button is enabled if we have liveLogsData corresponding to it
                isExpandLogsViewEnabled = true;
            } else if (this.hasTaskCompleted(task) && task.logUrl) {
                isExpandLogsViewEnabled = true;
            } else {
                isExpandLogsViewEnabled = false;
            }
        }
        return isExpandLogsViewEnabled;
    }

    public static isLiveComponentVisible(task: ITaskLog): boolean {
        let isVisible: boolean = false;
        if (this.isTaskInProgress(task) && !!task.liveLogsData) {
            isVisible = true;
        }
        return isVisible;
    }

    public static hasTaskCompleted(task: ITaskLog): boolean {
        let isCompleted: boolean = true;
        const taskState = task.state;
        isCompleted = !this.isTaskInProgress(task)
            && taskState !== TaskState.Pending
            && taskState !== TaskState.Skipped
            && taskState !== TaskState.Unknown;
        return isCompleted;
    }

    public static isTaskInProgress(task: ITaskLog): boolean {
        return task.state === TaskState.InProgress;
    }

    public static getLogsFromLiveLogData(liveLogData: ILiveLogsData | undefined): string {
        const logs: string[] = [];
        if (liveLogData && liveLogData.logLines) {
            for (const line of liveLogData.logLines) {
                // Since logs are already categorized and monaco uses regular expression
                // to identify type of log. Construct the log with tags again
                if (line.logType === LogType.Default) {
                    logs.push(line.content);
                } else {
                    logs.push((line.logType ? "[" + line.logType + "]" : "") + line.content);
                }
            }
        }
        const serializedLogs: string = logs.join("\n");
        return serializedLogs;
    }

    public static fixTasksRank(tasks: ITaskLog[]): void {
        if (tasks && tasks.length > 0) {
            let sameRankFound: boolean = false;
            tasks.sort((task1: ITaskLog, task2: ITaskLog) => {
                if (task1.rank !== task2.rank) {
                    return task1.rank - task2.rank;
                } else if (!task2.startTime || !task1.startTime) {
                    sameRankFound = true;
                    return 0;
                } else {
                    sameRankFound = true;
                    if (task1.startTime > task2.startTime) {
                        return 1;
                    }
                    else if (task1.startTime < task2.startTime) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                }
            });

            if (sameRankFound) {
                // We don't want to touch ranks if no same ranks are found.
                for (let i = 0; i < tasks.length; i++) {
                    tasks[i].rank = i + 1;
                }
            }
        }
    }

    public static getLogLinesFromIssues(issues: Issue[], maxIssuesLineToShow: number): ILogLine[] {
        const logLines: ILogLine[] = [];
        if (issues && issues.length > 0) {
            const displayCount: number = this._getIssuesCountToDisplay(issues, maxIssuesLineToShow);
            for (let i = 0; i < displayCount; i++) {
                const issue = issues[i];
                // Don't create issue log line when no error message present
                if (issue && issue.message) {
                    const logLine: ILogLine = {
                        content: issue.message,
                        logType: issue.issueType === IssueType.Error ? LogType.Error : LogType.Warning,
                        lineNumber: this._getLogLineNumber(issue)
                    };
                    logLines.push(logLine);
                }
            }

            if (displayCount < issues.length) {
                const skippedIssuesCount = issues.length - displayCount;
                if (skippedIssuesCount > 0) {
                    const moreErrorsLogLine: ILogLine = {
                        content: format(Resources.MoreErrorsMessage, skippedIssuesCount),
                        logType: LogType.Error
                    };

                    logLines.push(moreErrorsLogLine);
                }
            }
        }
        return logLines;
    }

    public static getIssuesWithType(issueType: IssueType, task: ITaskLog): Issue[] {
        let issues: Issue[] = [];
        if (task && task.issues) {
            issues = task.issues.filter((issue: Issue) => {
                return issue.issueType === issueType && issue.message;
            });
        }
        return issues;
    }

    public static fetchLogs(taskLog: ITaskLog, refresh?: boolean): Promise<string> {
        if (!taskLog.logUrl || !taskLog.fetchLogDelegate) {
            const defaultLogContent = localeFormat("{0} {1}", taskLog.name, LogsViewUtility.getTaskStatus(taskLog.state, 0).status.toLocaleLowerCase());
            return Promise.resolve(defaultLogContent);
        }

        // Task is completed cache the promise
        const cacheKey: string = taskLog.logUrl.toLowerCase();

        let promise: Promise<string> | null;
        if (!this._cachedLogs.hasOwnProperty(cacheKey) || refresh) {
            promise = taskLog.fetchLogDelegate() as Promise<string>;
            // For Server task, logUrl get generated even before task complete so
            // cache promise only when task is completed
            if (this.hasTaskCompleted(taskLog)) {
                this._cachedLogs[cacheKey] = promise;
            }
        } else {
            promise = this._cachedLogs[cacheKey];
        }

        /* tslint:disable:promise-must-complete 
           Refer to the limitations presented by HamletDRC - https://github.com/Microsoft/tslint-microsoft-contrib/issues/26
           This is in line with the situation here, as logsPromise is supposed to be resolved after promise is resolved, and not
           in the sync code block
        */
        let logsPromiseResolve: (value: string | PromiseLike<string> | undefined) => void;
        const logsPromise = new Promise<string>((resolve, reject) => { logsPromiseResolve = resolve; });
        /* tslint:enable:promise-must-complete */

        promise.then((logs: string) => {
            logsPromiseResolve(logs);
        }, (error) => {
            const errorMessage = (error && error.message) ? error.message : error;
            logsPromiseResolve(errorMessage);
        });

        return logsPromise;
    }

    public static getTimeDiffFriendlyString(startTime: Date | undefined, finishTime: Date | undefined): string {
        startTime = startTime || new Date();
        finishTime = finishTime || new Date();
        const dateDiff: number = Math.abs(startTime.valueOf() - finishTime.valueOf());
        const seconds: number = dateDiff / 1000;
        const minutes: number = seconds / 60;

        if (minutes < 2) {
            return format(Resources.DurationFormatSeconds, Math.round(seconds));
        }

        const hours: number = minutes / 60;

        if (hours < 2) {
            return format(Resources.DurationFormatMinutes, (Math.round(minutes * 10) / 10).toLocaleString());
        }

        const days: number = hours / 24;
        if (days < 2) {
            return format(Resources.DurationFormatHours, (Math.round(hours * 10) / 10).toLocaleString());
        }

        return format(Resources.DurationFormatDays, Math.round(days));
    }

    public static splitLogLine(line: string): string[] {
        let lines: string[] = [];
        if (line) {
            lines = line.split(/\r\n|\r|\n/g);
        }
        return lines;
    }

    /**
     * Return all tasks which are either completed or in-progress, its filter out tasks with pending, skipped and unknown state
     * @param tasks 
     */
    public static getSortedValidTasks(tasks: ITaskLog[]): ITaskLog[] {
        let taskLogs: ITaskLog[] = [];
        if (tasks && tasks.length > 0) {
            taskLogs = tasks.filter((taskLog: ITaskLog) => {
                const taskState = taskLog.state;
                return taskState !== TaskState.Pending
                    && taskState !== TaskState.Skipped
                    && taskState !== TaskState.Unknown;
            });

            if (taskLogs && taskLogs.length > 0) {
                taskLogs = taskLogs.sort((task1: ITaskLog, task2: ITaskLog) => {
                    return task1.rank - task2.rank;
                });
            }
        }

        return taskLogs;
    }

    public static getTaskIndexForGivenRank(rank: number, tasks: ITaskLog[]): number {
        let taskIndex: number = -1;
        if (tasks && tasks.length > 0) {
            const length: number = tasks.length;
            for (let i: number = 0; i < length; i++) {
                if (tasks[i].rank === rank) {
                    taskIndex = i;
                    break;
                }
            }
        }
        return taskIndex;
    }

    public static getNextTask(tasks: ITaskLog[], currentTaskRank: number): ITaskLog | null {
        let nextTaskIndex: number = -1;
        const validTasks: ITaskLog[] = LogsViewUtility.getSortedValidTasks(tasks);
        if (validTasks && validTasks.length > 0) {
            const expandedTaskIndex = LogsViewUtility.getTaskIndexForGivenRank(currentTaskRank, validTasks);

            if (expandedTaskIndex >= 0 && expandedTaskIndex < validTasks.length - 1) {
                nextTaskIndex = expandedTaskIndex + 1;
            }
        }

        if (nextTaskIndex >= 0) {
            return validTasks[nextTaskIndex];
        }
        return null;
    }

    public static getPreviousTask(tasks: ITaskLog[], currentTaskRank: number): ITaskLog | null {
        let prevTaskIndex: number = -1;
        const validTasks: ITaskLog[] = LogsViewUtility.getSortedValidTasks(tasks);
        if (validTasks && validTasks.length > 0) {
            const expandedTaskIndex = LogsViewUtility.getTaskIndexForGivenRank(currentTaskRank, validTasks);

            if (expandedTaskIndex > 0) {
                prevTaskIndex = expandedTaskIndex - 1;
            }
        }

        if (prevTaskIndex >= 0) {
            return validTasks[prevTaskIndex];
        }
        return null;
    }

    public static getIndexOfTaskToBeMovedToView(phaseJobState: JobStates, tasks: ITaskLog[]): number {
        let taskIndex: number = 0;
        if (phaseJobState && tasks && tasks.length > 0) {
            switch (phaseJobState) {
                case JobStates.Failed:
                    taskIndex = this._getIndexOfFirstTaskWithState(tasks, [TaskState.Cancelled, TaskState.Failed]);
                    break;
                case JobStates.PartiallySucceeded:
                    taskIndex = this._getIndexOfFirstTaskWithState(tasks, [TaskState.PartiallySucceeded]);
                    break;
                case JobStates.InProgress:
                    taskIndex = this._getIndexofTaskWithLiveLog(tasks);
                    break;
                default:
                    taskIndex = -1;
                    break;
            }
        }
        return taskIndex;
    }

    public static downloadLog(url: string) {
        if (url) {
            const newTab = window.open(url, "_self");
            if (newTab) {
                newTab.opener = null;
            }
        }
    }

    private static _getIndexOfFirstTaskWithState(tasks: ITaskLog[], states: TaskState[]): number {
        let taskIndex: number = 0;
        if (tasks && tasks.length > 0) {
            let firstTask: ITaskLog | null = null;

            tasks.some((task: ITaskLog) => {
                let isAcceptableState: boolean = false;
                for (const state of states) {
                    if (task.state === state) {
                        isAcceptableState = true;
                        firstTask = task;
                        break;
                    }
                }

                return isAcceptableState;
            });

            if (!!firstTask) {
                taskIndex = tasks.findIndex((task: ITaskLog) => {
                    return !!firstTask && task.taskId === firstTask.taskId;
                });
            }
        }
        return taskIndex;
    }

    private static _getIndexofTaskWithLiveLog(tasks: ITaskLog[]): number {
        let taskIndex: number = -1;
        if (tasks && tasks.length > 0) {
            taskIndex = tasks.findIndex((task: ITaskLog) => {
                const liveLogsData = task.liveLogsData;
                return (task.state === TaskState.InProgress && liveLogsData && liveLogsData.logLines && liveLogsData.logLines.length > 0) || false;
            });
        }
        return taskIndex;
    }

    private static _getErrorString(count: number): string {
        return count > 1 ? Resources.Errors : Resources.Error;
    }

    private static _getWarningString(count: number): string {
        return count > 1 ? Resources.Warnings : Resources.Warning;
    }

    private static _getIssuesCountToDisplay(issues: Issue[], maxLines: number): number {
        if (issues && issues.length > 0) {
            let issuesCountToDisplay: number = 0;
            let totalLines: number = 0;
            for (let i: number = 0, len = issues.length; i < len; i++) {
                const logLines: string[] = this.splitLogLine(issues[i].message);
                totalLines += logLines.length;
                if (totalLines > maxLines) {
                    break;
                }
                issuesCountToDisplay++;
            }
            // We need to show atleast one Error even if it overflows
            return Math.max(issuesCountToDisplay, 1);
        } else {
            return 0;
        }
    }

    private static _getLogLineNumber(issue: Issue): number | null {
        let lineNumber: number | null = null;
        if (issue) {
            const issueData = issue.data;
            if (issueData && issueData[_logFileLineNumber]) {
                lineNumber = parseInt(issueData[_logFileLineNumber]);
                if (isNaN(lineNumber)) {
                    lineNumber = null;
                }
            }
        }
        return lineNumber;
    }

    private static _getUrlForRoute(pageContext: IVssPageContext | undefined, routeId: string, params: { [key: string]: any }): string {
        if (!pageContext) {
            return "";
        }

        const currentRouteValues = this._getCurrentRouteValues(pageContext);
        const routeValues: { [key: string]: string } = { project: currentRouteValues.project };
        if (currentRouteValues.team) {
            routeValues.team = currentRouteValues.team;
        }

        const uri = new Uri(routeUrl(pageContext, routeId, routeValues));
        for (const key in params) {
            if (params[key]) {
                uri.addQueryParam(key, params[key]);
            }
        }

        return uri.absoluteUri;
    }

    private static _getCurrentRouteValues(pageContext: IVssPageContext): { [key: string]: string } {
        const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
        return navigationService.getCurrentRoute().routeValues;
    }

    private static _cachedLogs: { [key: string]: Promise<string> } = {};
}
